import { useAuth } from 'react-oidc-context';
import { Capacitor } from '@capacitor/core';
import { ximiOidcClient, ximiUserManager } from './oidcConfig';
import { openXimiAuthBrowser } from './openXimiAuthBrowser';

/** Optional tenant + login pushed to the SSO page (pre-fill) via OIDC query params. */
export interface XimiLoginHints {
    tenantCode: string;
    loginHint: string;
}

/**
 * Single entry point for Ximi SSO auth in the app. Wraps react-oidc-context's
 * useAuth() to expose a small, stable surface (login / logout / token / state).
 *
 * Web → full-page redirect (react-oidc-context). Native → open the /authorize URL in the
 * system browser (openXimiAuthBrowser), then signinCallback — here on iOS, through the
 * deeplink on Android (useXimiDeeplink).
 */
export function useXimiSSOAuth() {
    const auth = useAuth();

    const login = async (hints?: XimiLoginHints) => {
        // 'tenant-code' & 'login_hint' are the params the AuthProvider reads to pre-fill
        // its login page. Ignored by the dev mock — only visible in QA.
        const extraQueryParams = hints
            ? { 'tenant-code': hints.tenantCode, login_hint: hints.loginHint }
            : undefined;

        if (Capacitor.isNativePlatform()) {
            // request_type 'si:r' so signinCallback routes to the redirect-callback handler
            // (else: "invalid request_type in state").
            // prompt 'login': the Android Custom Tab shares Chrome's SSO cookie, which survives
            // the sign-outs that skip end_session (session lost, offline logout) → always ask
            // for the password, so a shared device never signs the previous person back in.
            const request = await ximiOidcClient.createSigninRequest({
                request_type: 'si:r',
                prompt: 'login',
                extraQueryParams
            });
            const callbackUrl = await openXimiAuthBrowser(request.url);
            if (callbackUrl) await ximiUserManager.signinCallback(callbackUrl);
            return;
        }
        await auth.signinRedirect({ extraQueryParams });
    };

    return {
        login,
        logout: () => auth.signoutRedirect(),
        token: auth.user?.access_token ?? null,
        isAuthenticated: auth.isAuthenticated,
        isLoading: auth.isLoading,
        user: auth.user ?? null,
        error: auth.error ?? null
    };
}
